import React, { useMemo, useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calculator, Calendar, Clock, Mail, Phone, HelpCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import { ShareButtons } from '../components/ShareButtons';
import { useJsonLd } from '../hooks/useJsonLd';
import { getPostBySlug } from '../data/blogPosts';
import { getPublicBlog } from '../utils/api';

const SITE_URL = 'https://www.rubiconfinservices.com';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/7akuxub4_LOGO.png';

const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

const BlogImage = ({ image }) => {
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed]);
  const align = image.align || 'center';
  const wrapCls =
    align === 'left' ? 'sm:float-left sm:w-2/5 sm:mr-6 mb-4' :
    align === 'right' ? 'sm:float-right sm:w-2/5 sm:ml-6 mb-4' :
    'mx-auto sm:w-4/5 my-6';
  return (
    <>
      <figure className={`${wrapCls} w-full`}>
        <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <img
            src={image.url}
            alt={image.caption || 'blog visual'}
            loading="lazy"
            onClick={() => setZoomed(true)}
            className="w-full cursor-zoom-in transition-transform duration-300 ease-out hover:scale-110"
            data-testid="blog-image"
          />
        </div>
        {image.caption && <figcaption className="text-sm text-slate-500 italic mt-2 text-center">{image.caption}</figcaption>}
      </figure>

      {zoomed && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 cursor-zoom-out animate-in fade-in duration-200"
          onClick={() => setZoomed(false)}
          data-testid="blog-image-lightbox"
        >
          <button
            type="button"
            onClick={() => setZoomed(false)}
            className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="blog-image-lightbox-close"
          >
            <X size={32} />
          </button>
          <figure className="max-w-6xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={image.url} alt={image.caption || 'blog visual'} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" />
            {image.caption && <figcaption className="text-sm text-slate-200 italic mt-3 text-center">{image.caption}</figcaption>}
          </figure>
        </div>
      )}
    </>
  );
};

const Section = ({ section, index }) => (
  <section className="mb-10" data-testid={`blog-section-${index}`}>
    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{section.heading}</h2>
    {section.images?.map((img, i) => <BlogImage key={`img-${i}`} image={img} />)}
    {section.paragraphs?.map((p, i) => (
      <p key={i} className="text-base md:text-lg text-slate-700 leading-relaxed mb-4">{p}</p>
    ))}
    {section.bullets && (
      <ul className="space-y-3 mt-2">
        {section.bullets.map((b, i) => (
          <li key={i} className="flex gap-3 text-base md:text-lg text-slate-700 leading-relaxed">
            <span className="mt-2.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
            <span>{b.title && <strong className="text-slate-900">{b.title} </strong>}{b.text}</span>
          </li>
        ))}
      </ul>
    )}
    {section.numbered && (
      <ol className="space-y-4 mt-2">
        {section.numbered.map((n, i) => (
          <li key={i} className="flex gap-4 text-base md:text-lg text-slate-700 leading-relaxed">
            <span className="w-8 h-8 rounded-full bg-blue-900 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">{i + 1}</span>
            <span><strong className="text-slate-900">{n.title} </strong>{n.text}</span>
          </li>
        ))}
      </ol>
    )}
    <div className="clear-both" />
  </section>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const staticPost = getPostBySlug(slug);
  const [post, setPost] = useState(staticPost || null);
  const [loading, setLoading] = useState(!staticPost);
  const postUrl = `${SITE_URL}/blog/${slug}`;

  useEffect(() => {
    if (staticPost) return;
    setLoading(true);
    getPublicBlog(slug)
      .then((p) => setPost(p))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug, staticPost]);

  const jsonLd = useMemo(() => {
    if (!post) return null;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: post.title,
          description: post.metaDescription,
          datePublished: post.date,
          dateModified: post.date,
          mainEntityOfPage: postUrl,
          image: LOGO_URL,
          author: { '@type': 'Organization', name: 'Rubicon Finservices', url: SITE_URL },
          publisher: { '@type': 'Organization', name: 'Rubicon Finservices', logo: { '@type': 'ImageObject', url: LOGO_URL } }
        },
        ...(post.faqs?.length ? [{
          '@type': 'FAQPage',
          mainEntity: post.faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
          }))
        }] : [])
      ]
    };
  }, [post, postUrl]);
  useJsonLd('blog-post-jsonld', jsonLd);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="blog-post-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div>
      </div>
    );
  }
  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div data-testid="blog-post-page">
      <SEO
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        keywords={post.keywords}
        ogImage="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/7akuxub4_LOGO.png"
      />
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-6 text-sm font-semibold" data-testid="blog-back-link">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          <div className="flex flex-wrap items-center gap-4 text-sm text-blue-200 mb-4">
            <span className="bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full font-semibold text-xs">{post.category}</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {post.readTime}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="blog-post-title">{post.title}</h1>
        </div>
      </section>

      <article className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-12 border-l-4 border-amber-500 pl-6">{post.excerpt}</p>
          {post.sections.map((s, i) => <Section key={i} section={s} index={i} />)}

          {post.faqs?.length > 0 && (
            <section className="mb-10" data-testid="blog-faq-section">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <HelpCircle className="text-amber-600" size={28} /> Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="border-2 border-slate-200 rounded-xl px-6 bg-slate-50">
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`blog-faq-item-${i}`}>
                    <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-slate-900 hover:text-amber-600 hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-base text-slate-700 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          <div className="mb-10 pt-8 border-t border-slate-200">
            <ShareButtons title={post.title} url={postUrl} />
          </div>

          <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-6 text-slate-700 text-sm space-y-2" data-testid="blog-contact-box">
            <p className="flex items-center gap-2"><Mail size={16} className="text-amber-600" /> <a href="mailto:communications@rubiconfinservices.com" className="hover:text-amber-600">communications@rubiconfinservices.com</a></p>
            <p className="flex items-center gap-2"><Phone size={16} className="text-amber-600" /> <a href="tel:+913340442577" className="hover:text-amber-600">+91-33-40442577</a></p>
            <p className="font-semibold text-slate-900">ARN: 102867</p>
          </div>
        </div>
      </article>

      <section className="py-16 bg-gradient-to-r from-blue-900 to-purple-900 text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to start your first SIP?</h2>
          <p className="text-base md:text-lg text-blue-100 mb-8">Estimate your SIP growth or talk to our Kolkata team for a plain-language conversation.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/calculators" data-testid="blog-cta-calculator">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                <Calculator className="mr-2" size={20} /> Try the SIP Calculator
              </Button>
            </Link>
            <Link to="/contact" data-testid="blog-cta-contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg">
                Contact Us <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-blue-200 mt-8">Mutual Fund investments are subject to market risks, read all scheme related documents carefully.</p>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
